/**
 * Validates a color string to prevent CSS injection attacks.
 * Allows hex colors and linear-gradient values with hex colors.
 */
const isValidColorValue = (value: string): boolean => {
    const trimmed = value.trim();
    // Match hex colors: #RGB, #RRGGBB, #RRGGBBAA
    const hexPattern = /^#[0-9a-fA-F]{3,8}$/;
    // Match linear-gradient with hex colors and percentages (safe subset)
    const gradientPattern =
        /^linear-gradient\s*\(\s*\d+deg\s*,\s*(#[0-9a-fA-F]{3,8}\s+\d+%\s*,?\s*)+\)$/;

    return hexPattern.test(trimmed) || gradientPattern.test(trimmed);
};

/**
 * Parses and validates a colour filter item's label (comma-separated hex
 * colours or a raw linear-gradient), returning a safe CSS background value.
 * Returns a fallback colour if validation fails.
 */
export const getSafeSwatchBackground = (
    label: string | null | undefined,
): string => {
    if (!label) return '#fff';

    // If label starts with #, it might be comma-separated hex colors
    if (label.startsWith('#')) {
        const colors = label.split(',').filter(isValidColorValue);
        if (colors.length === 0) return '#fff';

        const firstColor = colors[0];
        if (!firstColor) return '#fff';

        return colors.length > 1
            ? `linear-gradient(to right, ${colors.join(', ')})`
            : firstColor;
    }

    // If label is a linear-gradient, validate it
    if (label.startsWith('linear-gradient') && isValidColorValue(label)) {
        return label;
    }

    // Fallback for invalid values
    return '#fff';
};

/**
 * The human-readable name of a colour filter item. Colour items carry their
 * swatch CSS in `label` and their name in `value` — the reverse of every
 * other list filter.
 */
export const getColorItemName = (value: string | number): string => {
    const name = String(value);
    return name.charAt(0).toUpperCase() + name.slice(1);
};
